import { useState } from "react";
import clsx from "clsx";
import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";

import { ITicketInsightCluster } from "../../../models/Tickets/TicketInsight";
import { pillBaseClasses } from "../ticketMeta";
import InsightTicketRow from "./InsightTicketRow";
import { Card, EmptyState } from "./InsightWidgets";
import { formatSimilarity } from "./insightMeta";

interface SimilarIssuesCardProps {
  clusters: ITicketInsightCluster[];
  threshold: number;
  onThresholdChange: (threshold: number) => void;
}

export const similarityPresets: { label: string; value: number; hint: string }[] = [
  { label: "Loose", value: 0.72, hint: "Groups more; expect some false friends" },
  { label: "Normal", value: 0.78, hint: "Same problem, different words" },
  { label: "Strict", value: 0.85, hint: "Near-identical reports only" },
];

/**
 * Groups of tickets whose wording means the same thing, found by comparing
 * embeddings rather than topic labels - so two tickets the model labelled
 * slightly differently still land together if they describe the same fault.
 */
export default function SimilarIssuesCard({
  clusters,
  threshold,
  onThresholdChange,
}: SimilarIssuesCardProps) {
  const [expanded, setExpanded] = useState<number | null>(
    clusters.length > 0 ? clusters[0].id : null,
  );

  const duplicates = clusters.filter((c) => c.likelyDuplicate).length;
  const crossCustomer = clusters.filter((c) => c.customerCount > 1).length;

  return (
    <Card
      title="Similar issues"
      description={`Tickets that describe the same problem, grouped by meaning. ${
        clusters.length
      } group${clusters.length === 1 ? "" : "s"}${
        crossCustomer ? `, ${crossCustomer} affecting more than one customer` : ""
      }${duplicates ? `, ${duplicates} with likely duplicates` : ""}.`}
      action={
        <div className="flex items-center gap-1 text-xs">
          <span className="text-gray-500">Grouping:</span>
          {similarityPresets.map((preset) => (
            <button
              key={preset.value}
              type="button"
              title={preset.hint}
              onClick={() => onThresholdChange(preset.value)}
              className={clsx(
                "rounded border px-2 py-0.5",
                Math.abs(threshold - preset.value) < 0.001
                  ? "border-orange-500 bg-orange-50 text-orange-800"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50",
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      }
    >
      {clusters.length === 0 ? (
        <EmptyState>
          No two tickets in scope are similar enough at this setting. Try a
          looser grouping, or analyse more tickets.
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {clusters.map((cluster) => {
            const open = expanded === cluster.id;
            return (
              <div
                key={cluster.id}
                className={clsx(
                  "rounded-md border",
                  cluster.likelyDuplicate
                    ? "border-amber-300"
                    : "border-gray-200",
                )}
              >
                <button
                  type="button"
                  onClick={() => setExpanded(open ? null : cluster.id)}
                  className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-900">
                      {cluster.label}
                    </span>
                    <span className={clsx(pillBaseClasses, "bg-orange-100 text-orange-800")}>
                      {cluster.count} tickets
                    </span>
                    {cluster.active > 0 ? (
                      <span className={clsx(pillBaseClasses, "bg-blue-100 text-blue-700")}>
                        {cluster.active} active
                      </span>
                    ) : null}
                    {cluster.customerCount > 1 ? (
                      <span className={clsx(pillBaseClasses, "bg-violet-100 text-violet-700")}>
                        {cluster.customerCount} customers
                      </span>
                    ) : (
                      <span className={clsx(pillBaseClasses, "bg-gray-100 text-gray-600")}>
                        1 customer
                      </span>
                    )}
                    {cluster.likelyDuplicate ? (
                      <span
                        className={clsx(pillBaseClasses, "gap-1 bg-amber-100 text-amber-800")}
                        title="Two or more near-identical tickets from the same customer"
                      >
                        <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                        Likely duplicate
                      </span>
                    ) : null}
                  </div>
                  <span
                    className="text-xs text-gray-500"
                    title="Average similarity between tickets in the group"
                  >
                    Cohesion {formatSimilarity(cluster.cohesion)}
                  </span>
                </button>
                {open ? (
                  <div className="divide-y divide-gray-100 border-t border-gray-100 px-3">
                    {cluster.tickets.map((member) => (
                      <InsightTicketRow
                        key={member.ticket.ticketId}
                        ticket={member.ticket}
                        extra={
                          <span
                            className="text-xs text-gray-500"
                            title="Similarity to the closest ticket in this group"
                          >
                            {formatSimilarity(member.similarity)} match
                          </span>
                        }
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
